import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import RoleService from '../../../services/roleService';
import { fetchAllFeatures } from '../../../services/menuService';

// ---------------------------------------------------------------------------
// Types (extracted from the retired MenuOrderManager)
// ---------------------------------------------------------------------------

export interface MenuNode {
  id: string;
  name: string;
  path: string;
  icon?: string;
  subItems?: MenuNode[];
  parentKey?: string;
  permissionKey?: string;
}

export interface MenuItemState {
  enabled: boolean;
  hidden: boolean;
  icon?: string;
}

interface MenuOrderItem {
  menuKey: string;
  order: number;
  enabled?: boolean;
  hidden?: boolean;
  icon?: string;
  subItems?: Array<{
    subMenuKey: string;
    order: number;
    enabled?: boolean;
    hidden?: boolean;
    icon?: string;
  }>;
}

interface MenuOrderPayload {
  menuItems: MenuOrderItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Apply a saved menu order to the permission-filtered items — only items
 *  explicitly in the saved order are shown once an order exists. */
function applyMenuOrder(
  items: MenuNode[],
  menuOrder?: { menuItems: MenuOrderItem[] },
): MenuNode[] {
  if (!menuOrder || !menuOrder.menuItems || menuOrder.menuItems.length === 0) {
    return items;
  }

  const orderMap = new Map(menuOrder.menuItems.map((order) => [order.menuKey, order]));

  return items
    .filter((item) => orderMap.has(item.id))
    .map((item) => {
      const order = orderMap.get(item.id);
      const savedSubItems = order?.subItems;
      return {
        ...item,
        order: order?.order ?? 999,
        icon: order?.icon || item.icon,
        subItems: item.subItems
          ? item.subItems
              .filter((subItem) => {
                if (!savedSubItems || savedSubItems.length === 0) return true;
                return savedSubItems.some((s) => s.subMenuKey === subItem.id);
              })
              .map((subItem) => {
                const subOrder = savedSubItems?.find((s) => s.subMenuKey === subItem.id);
                return {
                  ...subItem,
                  order: subOrder?.order ?? 999,
                  icon: subOrder?.icon || subItem.icon,
                };
              })
              .sort(
                (a, b) =>
                  ((a as MenuNode & { order?: number }).order ?? 999) -
                  ((b as MenuNode & { order?: number }).order ?? 999),
              )
          : undefined,
      };
    })
    .sort(
      (a, b) =>
        ((a as MenuNode & { order?: number }).order ?? 999) -
        ((b as MenuNode & { order?: number }).order ?? 999),
    );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMenuConfig(roleId: string) {
  const [menuItems, setMenuItems] = useState<MenuNode[]>([]);
  const [allFeatures, setAllFeatures] = useState<MenuNode[]>([]);
  const [menuStates, setMenuStates] = useState<Map<string, MenuItemState>>(new Map());
  const [subMenuStates, setSubMenuStates] = useState<Map<string, Map<string, MenuItemState>>>(
    new Map(),
  );
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [roleName, setRoleName] = useState<string>('');
  // Default (system) roles are locked — menu configuration is read-only.
  const [isDefaultRole, setIsDefaultRole] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Serialized snapshot of the last loaded/saved payload — dirty comparison base.
  const baselineRef = useRef<string>('');
  const [reloadKey, setReloadKey] = useState(0);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [response, allFeaturesResponse] = await Promise.all([
          RoleService.getRoleMenuOrder(roleId),
          fetchAllFeatures(),
        ]);
        if (cancelled) return;

        const rawFeatures = Array.isArray(allFeaturesResponse)
          ? allFeaturesResponse
          : ((allFeaturesResponse as { docs?: unknown[]; data?: unknown[] }).docs ??
            (allFeaturesResponse as { docs?: unknown[]; data?: unknown[] }).data ??
            []);
        setAllFeatures(
          (rawFeatures as Array<Record<string, unknown>>).map((f) => ({
            id: String(f['key'] ?? f['id'] ?? ''),
            name: String(f['name'] ?? ''),
            path: String(f['route'] ?? f['path'] ?? ''),
            icon: (f['icon'] as string | undefined) ?? '',
            parentKey: f['parentKey'] as string | undefined,
            permissionKey: (f['permissionKey'] as string | undefined) ?? '',
          })),
        );

        if (response.success && response.data.menuItems) {
          setRoleName(response.data.roleName ?? '');
          setIsDefaultRole(!!(response.data as { isDefault?: boolean }).isDefault);
          const items = response.data.menuItems as MenuNode[];
          const orderedItems = applyMenuOrder(items, response.data.menuOrder);
          setMenuItems(orderedItems);

          const newMenuStates = new Map<string, MenuItemState>();
          const newSubMenuStates = new Map<string, Map<string, MenuItemState>>();

          if (response.data.menuOrder?.menuItems) {
            (response.data.menuOrder.menuItems as MenuOrderItem[]).forEach((orderItem) => {
              newMenuStates.set(orderItem.menuKey, {
                enabled: orderItem.enabled !== false,
                hidden: orderItem.hidden === true,
                ...(orderItem.icon ? { icon: orderItem.icon } : {}),
              });

              if (orderItem.subItems && orderItem.subItems.length > 0) {
                const subMap = new Map<string, MenuItemState>();
                orderItem.subItems.forEach((subItem) => {
                  subMap.set(subItem.subMenuKey, {
                    enabled: subItem.enabled !== false,
                    hidden: subItem.hidden === true,
                    ...(subItem.icon ? { icon: subItem.icon } : {}),
                  });
                });
                newSubMenuStates.set(orderItem.menuKey, subMap);
              }
            });
          }

          setMenuStates(newMenuStates);
          setSubMenuStates(newSubMenuStates);
          setExpandedItems(new Set());
          baselineRef.current = JSON.stringify(
            buildPayload(orderedItems, newMenuStates, newSubMenuStates),
          );
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load menu configuration';
        console.error('Failed to fetch menu items:', err);
        setError(message);
        toast.error('Failed to load menu configuration');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [roleId, reloadKey]);

  // ── Reorder ───────────────────────────────────────────────────────────────
  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setMenuItems((prev) => {
      const newItems = [...prev];
      const [removed] = newItems.splice(dragIndex, 1);
      if (!removed) return prev;
      newItems.splice(hoverIndex, 0, removed);
      return newItems;
    });
  }, []);

  const moveSubItem = useCallback((parentIndex: number, dragIndex: number, hoverIndex: number) => {
    setMenuItems((prev) => {
      const newItems = [...prev];
      const parent = newItems[parentIndex];
      if (parent?.subItems) {
        const newSubItems = [...parent.subItems];
        const [removed] = newSubItems.splice(dragIndex, 1);
        if (!removed) return prev;
        newSubItems.splice(hoverIndex, 0, removed);
        newItems[parentIndex] = { ...parent, subItems: newSubItems };
      }
      return newItems;
    });
  }, []);

  const setSequence = useCallback((menuId: string, newSequence: number) => {
    setMenuItems((prev) => {
      const currentIndex = prev.findIndex((item) => item.id === menuId);
      if (currentIndex === -1) return prev;
      const targetIndex = newSequence - 1;
      if (targetIndex < 0 || targetIndex >= prev.length || targetIndex === currentIndex) {
        return prev;
      }
      const newItems = [...prev];
      const [removed] = newItems.splice(currentIndex, 1);
      if (!removed) return prev;
      newItems.splice(targetIndex, 0, removed);
      return newItems;
    });
  }, []);

  const moveBy = useCallback((menuId: string, delta: -1 | 1) => {
    setMenuItems((prev) => {
      const i = prev.findIndex((item) => item.id === menuId);
      const j = i + delta;
      if (i === -1 || j < 0 || j >= prev.length) return prev;
      const newItems = [...prev];
      const a = newItems[i];
      const b = newItems[j];
      if (!a || !b) return prev;
      newItems[i] = b;
      newItems[j] = a;
      return newItems;
    });
  }, []);

  const setSubSequence = useCallback((parentId: string, subMenuId: string, newSequence: number) => {
    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id !== parentId || !item.subItems) return item;
        const subItems = [...item.subItems];
        const currentIndex = subItems.findIndex((sub) => sub.id === subMenuId);
        if (currentIndex === -1) return item;
        const targetIndex = newSequence - 1;
        if (targetIndex < 0 || targetIndex >= subItems.length || targetIndex === currentIndex) {
          return item;
        }
        const [removed] = subItems.splice(currentIndex, 1);
        if (!removed) return item;
        subItems.splice(targetIndex, 0, removed);
        return { ...item, subItems };
      }),
    );
  }, []);

  const moveSubBy = useCallback((parentId: string, subMenuId: string, delta: -1 | 1) => {
    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id !== parentId || !item.subItems) return item;
        const subItems = [...item.subItems];
        const i = subItems.findIndex((sub) => sub.id === subMenuId);
        const j = i + delta;
        if (i === -1 || j < 0 || j >= subItems.length) return item;
        const a = subItems[i];
        const b = subItems[j];
        if (!a || !b) return item;
        subItems[i] = b;
        subItems[j] = a;
        return { ...item, subItems };
      }),
    );
  }, []);

  // ── Expand / collapse ─────────────────────────────────────────────────────
  const toggleExpand = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) newSet.delete(itemId);
      else newSet.add(itemId);
      return newSet;
    });
  }, []);

  // ── Enabled / hidden toggles ──────────────────────────────────────────────
  const toggleEnabled = useCallback((menuId: string) => {
    setMenuStates((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(menuId) || { enabled: true, hidden: false };
      newMap.set(menuId, { ...current, enabled: !current.enabled });
      return newMap;
    });
  }, []);

  const toggleHidden = useCallback((menuId: string) => {
    setMenuStates((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(menuId) || { enabled: true, hidden: false };
      newMap.set(menuId, { ...current, hidden: !current.hidden });
      return newMap;
    });
  }, []);

  const toggleSubEnabled = useCallback((menuId: string, subMenuId: string) => {
    setSubMenuStates((prev) => {
      const newMap = new Map(prev);
      const newSubMap = new Map(newMap.get(menuId) || new Map<string, MenuItemState>());
      const current = newSubMap.get(subMenuId) || { enabled: true, hidden: false };
      newSubMap.set(subMenuId, { ...current, enabled: !current.enabled });
      newMap.set(menuId, newSubMap);
      return newMap;
    });
  }, []);

  const toggleSubHidden = useCallback((menuId: string, subMenuId: string) => {
    setSubMenuStates((prev) => {
      const newMap = new Map(prev);
      const newSubMap = new Map(newMap.get(menuId) || new Map<string, MenuItemState>());
      const current = newSubMap.get(subMenuId) || { enabled: true, hidden: false };
      newSubMap.set(subMenuId, { ...current, hidden: !current.hidden });
      newMap.set(menuId, newSubMap);
      return newMap;
    });
  }, []);

  // ── Icons ─────────────────────────────────────────────────────────────────
  const setIcon = useCallback((menuId: string, icon: string) => {
    setMenuStates((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(menuId) || { enabled: true, hidden: false };
      newMap.set(menuId, { ...current, icon });
      return newMap;
    });
  }, []);

  const setSubIcon = useCallback((menuId: string, subMenuId: string, icon: string) => {
    setSubMenuStates((prev) => {
      const newMap = new Map(prev);
      const newSubMap = new Map(newMap.get(menuId) || new Map<string, MenuItemState>());
      const current = newSubMap.get(subMenuId) || { enabled: true, hidden: false };
      newSubMap.set(subMenuId, { ...current, icon });
      newMap.set(menuId, newSubMap);
      return newMap;
    });
  }, []);

  // ── Add items ─────────────────────────────────────────────────────────────
  const addMenu = useCallback((feature: MenuNode) => {
    const newMenuItem: MenuNode = { ...feature, subItems: [] };
    setMenuItems((prev) => [...prev, newMenuItem]);
    setMenuStates((prev) => {
      const newMap = new Map(prev);
      newMap.set(newMenuItem.id, {
        enabled: true,
        hidden: false,
        ...(newMenuItem.icon ? { icon: newMenuItem.icon } : {}),
      });
      return newMap;
    });
    toast.success(`${newMenuItem.name} added to sidebar`);
  }, []);

  const addSubItem = useCallback((parentId: string, subItem: MenuNode) => {
    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id !== parentId) return item;
        const existingSubs = item.subItems || [];
        if (existingSubs.some((sub) => sub.id === subItem.id)) return item;
        return { ...item, subItems: [...existingSubs, subItem] };
      }),
    );
    setSubMenuStates((prev) => {
      const newMap = new Map(prev);
      const newSubMap = new Map(newMap.get(parentId) || new Map<string, MenuItemState>());
      newSubMap.set(subItem.id, {
        enabled: true,
        hidden: false,
        ...(subItem.icon ? { icon: subItem.icon } : {}),
      });
      newMap.set(parentId, newSubMap);
      return newMap;
    });
    toast.success(`${subItem.name} added as submenu`);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const availableTopLevel = useMemo(
    () =>
      allFeatures.filter(
        (feature) => !feature.parentKey && !menuItems.some((item) => item.id === feature.id),
      ),
    [allFeatures, menuItems],
  );

  const currentPayload = useMemo(
    () => buildPayload(menuItems, menuStates, subMenuStates),
    [menuItems, menuStates, subMenuStates],
  );

  const isDirty = !isLoading && JSON.stringify(currentPayload) !== baselineRef.current;

  const counts = useMemo(() => {
    let hidden = 0;
    let disabled = 0;
    for (const item of menuItems) {
      const st = menuStates.get(item.id) || { enabled: true, hidden: false };
      if (st.hidden) hidden += 1;
      if (!st.enabled) disabled += 1;
    }
    const total = menuItems.length;
    return { total, hidden, disabled, visible: total - hidden };
  }, [menuItems, menuStates]);

  // ── Save / discard ────────────────────────────────────────────────────────
  const save = useCallback(async (): Promise<boolean> => {
    if (isDefaultRole) {
      toast.info('This is a default role — its menu configuration is locked and cannot be changed.');
      return false;
    }
    try {
      setIsSaving(true);
      setError(null);
      await RoleService.updateRoleMenuOrder(roleId, currentPayload);
      baselineRef.current = JSON.stringify(currentPayload);
      toast.success('Menu configuration saved');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save menu configuration';
      console.error('Failed to save menu order:', err);
      setError(message);
      toast.error('Failed to save menu configuration');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [roleId, currentPayload, isDefaultRole]);

  const discard = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    // data
    menuItems,
    allFeatures,
    availableTopLevel,
    menuStates,
    subMenuStates,
    expandedItems,
    roleName,
    counts,
    // flags
    isDefaultRole,
    isLoading,
    isSaving,
    isDirty,
    error,
    // actions
    moveItem,
    moveSubItem,
    setSequence,
    moveBy,
    setSubSequence,
    moveSubBy,
    toggleExpand,
    toggleEnabled,
    toggleHidden,
    toggleSubEnabled,
    toggleSubHidden,
    setIcon,
    setSubIcon,
    addMenu,
    addSubItem,
    save,
    discard,
  };
}

export type MenuConfigApi = ReturnType<typeof useMenuConfig>;

// ---------------------------------------------------------------------------
// Payload builder (shared by save + dirty tracking)
// ---------------------------------------------------------------------------

function buildPayload(
  menuItems: MenuNode[],
  menuStates: Map<string, MenuItemState>,
  subMenuStates: Map<string, Map<string, MenuItemState>>,
): MenuOrderPayload {
  return {
    menuItems: menuItems.map((item, index) => {
      const state = menuStates.get(item.id) || {
        enabled: true,
        hidden: false,
        icon: item.icon,
      };
      return {
        menuKey: item.id,
        order: index + 1,
        enabled: state.enabled,
        hidden: state.hidden,
        icon: state.icon || item.icon,
        subItems: item.subItems
          ? item.subItems.map((subItem, subIndex) => {
              const subState = subMenuStates.get(item.id)?.get(subItem.id) || {
                enabled: true,
                hidden: false,
                icon: subItem.icon,
              };
              return {
                subMenuKey: subItem.id,
                order: subIndex + 1,
                enabled: subState.enabled,
                hidden: subState.hidden,
                icon: subState.icon || subItem.icon,
              };
            })
          : undefined,
      };
    }),
  };
}
